import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Home,
  Users,
  BookOpen,
  BarChart3,
  Settings,
  FileText,
  Shield,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Server,
  Eye,
  Plus,
  GraduationCap,
  Activity,
  Clock,
  Database,
} from "lucide-react";
import { getAdminDashboardData, getTopResourceProfiles, getSandboxTimeExtensions } from "../api/adminDashboardApi";
import { useNavigation } from "../App";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "../components/ui/page-header";
import paths from "../routes";
import { useNavigate } from "react-router-dom";
import { TIME_PERIOD_OPTIONS, convertPeriodToDays, getTimePeriodLabel } from "../utils/timeUtils";
//import useNavigation from "../components/Context/NavigationContext";
const mockStats = {
  totalUsers: 1245,
  activeUsers: 1180,
  totalCourses: 45,
  activeCourses: 38,
  totalProjects: 156,
  systemHealth: 98.5,
};

const mockCourses = [
  {
    id: "1",
    name: "Data Science 101",
    instructor: "Dr. A. Sharma",
    students: 30,
    status: "active",
  },
  {
    id: "2",
    name: "Machine Learning Basics",
    instructor: "Dr. B. Patel",
    students: 25,
    status: "active",
  },
  {
    id: "3",
    name: "Deep Learning Advanced",
    instructor: "Dr. C. Singh",
    students: 20,
    status: "active",
  },
];

function DashboardContent() {
//  const { navigateTo } = useNavigation();
  const navigate = useNavigate()
  const [resourceProfilePeriod, setResourceProfilePeriod] = useState("today");
  const [timeExtensionPeriod, setTimeExtensionPeriod] = useState("today");

const { data:dashboardData, isLoading, error } = useQuery({
  queryKey: ["adminDashboard"],
  queryFn: () => getAdminDashboardData(),
});

const { data: topResourceProfiles, isLoading: resourceProfilesLoading } = useQuery({
  queryKey: ["topResourceProfiles", resourceProfilePeriod],
  queryFn: () => getTopResourceProfiles(convertPeriodToDays(resourceProfilePeriod)),
});

const { data: sandboxTimeExtensions, isLoading: timeExtensionsLoading } = useQuery({
  queryKey: ["sandboxTimeExtensions", timeExtensionPeriod],
  queryFn: () => getSandboxTimeExtensions(convertPeriodToDays(timeExtensionPeriod)),
});

const COLORS = ["#4F46E5", "#10B981", "#F59E0B"];
console.log(dashboardData )
 const difficultyData = [
    { name: "Beginner", value: dashboardData?.projectsByDifficulty?.beginner || 0 },
    { name: "Intermediate", value: dashboardData?.projectsByDifficulty?.intermediate || 0 },
    { name: "Advanced", value: dashboardData?.projectsByDifficulty?.advanced || 0 },
  ];
  const storage = dashboardData?.systemPerformance?.storage || {
    used: 0,
    total: 0,
    percentage: 0,
    unit: "GB",
  };
  const remaining = storage.total - storage.used;

  const data = [
    { name: "Used", value: storage.used },
    { name: "Remaining", value: remaining > 0 ? remaining : 0 },
  ];

  const total = difficultyData.reduce((sum, item) => sum + item.value, 0);
  return (
    <div className="space-y-6">
      <PageHeader
        title="Admin Dashboard"
        subtitle="System overview and platform management"
      />

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-black">
              {dashboardData?.systemPerformance?.activeUsers || 0}
            </div>
            <p className="text-sm text-muted-foreground">Active Sessions</p>
            <p className="text-xs text-gray-500">
out              of {dashboardData?.totalUsers || 0}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-black">
              {/* Assuming active resource profiles from system performance */}
              {dashboardData?.systemPerformance?.activeUsers || 0}
            </div>
            <p className="text-sm text-muted-foreground">Active Resource Profile</p>
            <p className="text-xs text-gray-500">
              out of {dashboardData?.totalUsers || 0} 
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-black">
              {dashboardData?.totalUsers || 0}
            </div>
            <p className="text-sm text-muted-foreground">Total Users</p>
            <p className="text-xs text-green-600">+{dashboardData?.currentMonthUsers || 0} this month</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-black">
              {dashboardData?.activeCourses || 0}
            </div>
            <p className="text-sm text-muted-foreground">Active Courses</p>
            <p className="text-xs text-gray-500">
              out of {dashboardData?.totalCourses || 0}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-black">
              {dashboardData?.totalProjects || 0}
            </div>
            <p className="text-sm text-muted-foreground">Total Projects</p>
            <p className="text-xs text-green-500">+{dashboardData?.publishedProjects || 0} this month</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Analytics Section - Two cards in one row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sandbox Time Extensions - First Column (Pie Chart) */}
        <Card className="h-[500px] text-xs">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-bits-gold" />
                <CardTitle>Sandbox Time Extensions</CardTitle>
              </div>
              <Select value={timeExtensionPeriod} onValueChange={setTimeExtensionPeriod}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_PERIOD_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <CardDescription className="text-xs">
              Extension request status ({getTimePeriodLabel(timeExtensionPeriod)})
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col p-2 pt-0">
            {timeExtensionsLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">Loading time extensions...</div>
              </div>
            ) : sandboxTimeExtensions ? (
              <div className="flex-1 flex flex-col">
                {sandboxTimeExtensions.total > 0 ? (
                  <div className="flex-1 flex flex-col">
                    {/* Pie Chart Section */}
                    <div className="flex justify-center items-center flex-1">
                      <PieChart width={250} height={250}>
                        <Pie
                          data={[
                            { name: "Pending", value: sandboxTimeExtensions.pending, color: "#F59E0B" },
                            { name: "Approved", value: sandboxTimeExtensions.approved, color: "#10B981" },
                            { name: "Rejected", value: sandboxTimeExtensions.rejected, color: "#EF4444" },
                            { name: "Expired", value: sandboxTimeExtensions.expired, color: "#6B7280" },
                          ].filter(item => item.value > 0)}
                          cx="50%"
                          cy="50%"
                          outerRadius={100}
                          labelLine={false}
                          dataKey="value"
                        >
                          {[
                            { name: "Pending", value: sandboxTimeExtensions.pending, color: "#F59E0B" },
                            { name: "Approved", value: sandboxTimeExtensions.approved, color: "#10B981" },
                            { name: "Rejected", value: sandboxTimeExtensions.rejected, color: "#EF4444" },
                            { name: "Expired", value: sandboxTimeExtensions.expired, color: "#6B7280" },
                          ].filter(item => item.value > 0).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </div>

                    {/* Legend Section - Below Chart */}
                    <div className="flex flex-col space-y-2 text-[10px] px-4">
                      {[
                        { name: "Pending", value: sandboxTimeExtensions.pending, color: "#F59E0B" },
                        { name: "Approved", value: sandboxTimeExtensions.approved, color: "#10B981" },
                        { name: "Rejected", value: sandboxTimeExtensions.rejected, color: "#EF4444" },
                        { name: "Expired", value: sandboxTimeExtensions.expired, color: "#6B7280" },
                      ].filter(item => item.value > 0).map((item, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: item.color }}
                            ></div>
                            <span className="text-[10px] font-medium text-gray-700">{item.name}</span>
                          </div>
                          <span className="text-[10px] font-semibold text-gray-900">{item.value}</span>
                        </div>
                      ))}
                      <div className="border text-bits-blue font-semibold border-bits-blue rounded-sm flex justify-center items-center py-2 mt-2">
                        Total: {sandboxTimeExtensions.total}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-500">
                    No time extension requests for this period
                  </div>
                )}
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                No time extension data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Resource Profiles - Second Column (Bar Chart) */}
        <Card className="h-[500px]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="h-5 w-5 text-bits-blue" />
                <CardTitle>Top Resource Profiles</CardTitle>
              </div>
              <Select value={resourceProfilePeriod} onValueChange={setResourceProfilePeriod}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_PERIOD_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <CardDescription className="text-xs">
              Most used resource profiles ({getTimePeriodLabel(resourceProfilePeriod)})
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 p-2 pt-0">
            {resourceProfilesLoading ? (
              <div className="h-full flex items-center justify-center">Loading resource profiles...</div>
            ) : topResourceProfiles && topResourceProfiles.length > 0 ? (
              <div className="h-full text-xs">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={topResourceProfiles.map(profile => ({
                      name: profile.name.length > 6 ? profile.name.substring(0, 6) + '...' : profile.name,
                      fullName: profile.name,
                      Projects: profile.projectCount,
                      Students: profile.studentCount,
                      RAM: parseFloat(profile.ram.replace(/[^0-9.]/g, '')), // Extract number from "1.5 GB"
                      CPU: parseFloat(profile.cpu.replace(/[^0-9.]/g, '')), // Extract number from "1 Cores"
                    }))}
                    margin={{ top: 10, right: 10, left: 10, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      fontSize={12}
                    />
                    <YAxis />
                    <Tooltip
                      formatter={(value, name, props) => [
                        name === 'RAM' ? `${value} GB` :
                        name === 'CPU' ? `${value} Cores` : value,
                        name
                      ]}
                      labelFormatter={(label, payload) => {
                        // Normalize payload: Recharts may pass an array or an object depending on chart type
                        let data: any = null;
                        if (Array.isArray(payload)) {
                          data = payload[0]?.payload;
                        } else if (payload && (payload as any).payload) {
                          data = (payload as any).payload;
                        } else {
                          data = payload as any;
                        }

                        // Prefer fullName (original profile name) but fall back to provided label
                        return data?.fullName || label;
                      }}
                    />
                    <Legend />
                    
                    <Bar dataKey="Projects" fill="#4F46E5" name="Projects" />
                    <Bar dataKey="Students" fill="#10B981" name="Students" />
                    <Bar dataKey="RAM" fill="#F59E0B" name="RAM (GB)" />
                    <Bar dataKey="CPU" fill="#EF4444" name="CPU (Cores)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No resource profile data available for this period
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
        {/* <Card className="p-4 flex items-center justify-between shadow-md rounded-2xl">
      <div className="flex text-[14px] items-start justify-start w-full text-bits-blue font-semibold">
        Project split by Difficulty
      </div>
      <div className="flex justify-center items-center w-1/2">
        <PieChart width={160} height={160}>
          <Pie
            data={difficultyData}
            cx="50%"
            cy="50%"
            outerRadius={80}
            labelLine={false}
            dataKey="value"
          >
            {difficultyData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </div>

      <div className="w-1/2 flex flex-col justify-center space-y-2 text-[10px]">
        {difficultyData.map((item, index) => (
          <div key={index} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              ></div>
              <span className="text-[10px] font-medium text-gray-700">{item.name}</span>
            </div>
            <span className="text-[10px] font-semibold text-gray-900">{item.value}</span>
          </div>
        ))}
        <div className="w-flex border text-bits-blue font-semibold border-bits-blue rounded-sm  flex flex-col justify-center items-center space-y-2">
                Total : {total}
        </div>
      </div>

    </Card> */}
 {/* <Card className="p-4 flex flex-col items-center justify-center shadow-md rounded-2xl">
      <h3 className="text-[14px] -mt-2 w-full justify-start text-bits-blue font-semibold font-semibold text-gray-800 mb-2">System Storage</h3>

      <div className="relative flex justify-center items-center">
        <PieChart width={200} height={200}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={60}
            outerRadius={80}
            startAngle={90}
            endAngle={-270}
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
        </PieChart>

        <div className="absolute text-center">
          <p className="text-2xl font-bold text-bits-blue">
            {storage.percentage}%
          </p>
          <p className="text-xs text-gray-500">Used</p>
        </div>
      </div>

      <div className="mt-3 flex justify-between w-48 text-sm text-gray-700">
        <span>Used: {storage.used} {storage.unit}</span>
        <span>Total: {storage.total} {storage.unit}</span>
      </div>
    </Card> */}

      </div>

      {/* Security Alert */}
      {/* <Card className="border-red-200 bg-red-50">
        <CardContent className="p-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <div className="flex-1">
              <h4 className="font-medium text-red-800">Security Alert</h4>
              <p className="text-sm text-red-700">
                3 active security alerts require your attention
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-100"
              onClick={() => navigateTo("admin-audit")}
            >
              Review Alerts
            </Button>
          </div>
        </CardContent>
      </Card> */}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate(paths.manageProjects)}
        >
          <CardContent className="p-6 text-center">
            <FileText className="h-8 w-8 mx-auto mb-2 text-bits-blue" />
            <h3 className="font-semibold mb-1">Manage Projects</h3>
            <p className="text-sm text-muted-foreground">
              View and manage all platform projects
            </p>
          </CardContent>
        </Card>

        {/* <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigateTo("admin-reports")}
        >
          <CardContent className="p-6 text-center">
            <BarChart3 className="h-8 w-8 mx-auto mb-2 text-bits-gold" />
            <h3 className="font-semibold mb-1">Reports & Analytics</h3>
            <p className="text-sm text-muted-foreground">
              Generate comprehensive reports
            </p>
          </CardContent>
        </Card> */}

        {/* <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigateTo("admin-audit")}
        >
          <CardContent className="p-6 text-center">
            <Shield className="h-8 w-8 mx-auto mb-2 text-bits-red" />
            <h3 className="font-semibold mb-1">Audit & Security</h3>
            <p className="text-sm text-muted-foreground">
              Monitor system security and activities
            </p>
          </CardContent>
        </Card> */}

        <Card
          className="cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate(paths.gradebook)}
        >
          <CardContent className="p-6 text-center">
            <GraduationCap className="h-8 w-8 mx-auto mb-2 text-green-600" />
            <h3 className="font-semibold mb-1">Platform Gradebook</h3>
            <p className="text-sm text-muted-foreground">
              Monitor all student grades
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      {/* <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Recent Activity</CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigateTo("admin-audit")}
            >
              <Activity className="h-4 w-4 mr-2" />
              View All Audit Logs
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <div>
                <p className="font-medium">New course created</p>
                <p className="text-sm text-muted-foreground">
                  Dr. A. Sharma created "Advanced Data Science"
                </p>
              </div>
              <span className="text-xs text-muted-foreground ml-auto">
                2 hours ago
              </span>
            </div>

            <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
              <div>
                <p className="font-medium">Bulk user enrollment</p>
                <p className="text-sm text-muted-foreground">
                  25 new students added to Machine Learning course
                </p>
              </div>
              <span className="text-xs text-muted-foreground ml-auto">
                4 hours ago
              </span>
            </div>

            <div className="flex items-center space-x-3 p-3 bg-red-50 rounded-lg">
              <Shield className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-medium">Security event detected</p>
                <p className="text-sm text-muted-foreground">
                  Multiple failed login attempts from IP 203.122.45.89
                </p>
              </div>
              <span className="text-xs text-muted-foreground ml-auto">
                6 hours ago
              </span>
            </div>

            <div className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <div>
                <p className="font-medium">System alert</p>
                <p className="text-sm text-muted-foreground">
                  High AWS usage detected - automatic scaling initiated
                </p>
              </div>
              <span className="text-xs text-muted-foreground ml-auto">
                8 hours ago
              </span>
            </div>
          </div>
        </CardContent>
      </Card> */}

      {/* Platform Overview */}
      {/* <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Top Performing Courses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {mockCourses.map((course) => (
                <div
                  key={course.id}
                  className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{course.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {course.instructor} • {course.students} students
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge className="bg-green-100 text-green-800">
                      95% completion
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        navigateTo("view-course", { courseId: course.id })
                      }
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>System Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span>Server Uptime</span>
                <span className="font-semibold text-green-600">99.8%</span>
              </div>

              <div className="flex justify-between items-center">
                <span>Active Users</span>
                <span className="font-semibold">{mockStats.activeUsers}</span>
              </div>

              <div className="flex justify-between items-center">
                <span>Storage Usage</span>
                <span className="font-semibold">750GB / 1TB</span>
              </div>

              <div className="flex justify-between items-center">
                <span>API Response Time</span>
                <span className="font-semibold text-green-600">145ms</span>
              </div>

              <div className="flex justify-between items-center">
                <span>Security Alerts</span>
                <span className="font-semibold text-red-600">3 active</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div> */}
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <div className="p-6 space-y-6">
      <DashboardContent />
    </div>
  );
}
